// src/utils/helpers.js
import { STUDENT_STATUS, STATUS_COLORS, STATUS_ICONS, VALIDATION } from './constants';

/**
 * Get badge style based on student status
 */
export const getStatusBadgeStyle = (status, baseStyle) => {
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS[STUDENT_STATUS.PENDING];
  return {
    ...baseStyle,
    backgroundColor: statusColor.bg,
    color: statusColor.text,
    border: `1px solid ${statusColor.border}`
  };
};

/**
 * Get icon for student status
 */
export const getStatusIcon = (status) => {
  return STATUS_ICONS[status] || '❓';
};

/**
 * Format phone number to (XXX) XXX-XXXX
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = phoneNumber.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

/**
 * Check if form is valid (submitted within 1 year)
 */
export const isFormValid = (submissionDate) => {
  if (!submissionDate) return false;
  
  const date = submissionDate?.toDate ? submissionDate.toDate() : new Date(submissionDate);
  const validUntil = new Date();
  validUntil.setFullYear(validUntil.getFullYear() - 1);
  
  return date > validUntil;
};

/**
 * Get current day of week
 */
export const getCurrentDay = () => {
  return new Date().toLocaleDateString('en-US', { weekday: 'long' });
};

/**
 * Get current date formatted
 */
export const getCurrentDate = () => {
  return new Date().toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

/**
 * Check if student is scheduled for today
 */
export const isScheduledToday = (student) => {
  const today = getCurrentDay();
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0); // Set to midnight for accurate date comparison
  
  // Format today's date as MM-DD-YY for BH comparison
  const todayFormatted = `${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}-${String(todayDate.getFullYear()).slice(-2)}`;
  
  // Camp students - check multiple sources
  if (student.programSelection?.includes('Camp') || student.billingType === 'bright-horizons') {
    // BH students - check careDates array
    if (student.careDates && Array.isArray(student.careDates)) {
      return student.careDates.some(careDate => careDate.date === todayFormatted);
    }
    
    // Regular camp students - check date range
    if (student.campStartDate && student.campEndDate) {
      const startDate = new Date(student.campStartDate);
      const endDate = new Date(student.campEndDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return todayDate >= startDate && todayDate <= endDate;
    }
    
    return false;
  }
  
  // Saturday class students - only show on Saturdays
  if (student.programSelection?.includes('Saturday')) {
    return today === 'Saturday';
  }
  
  // Pickup/Dropoff students - check pickup days
  const pickupDays = student.pickupDays || '';
  if (typeof pickupDays === 'string') {
    return pickupDays.toLowerCase().includes(today.toLowerCase());
  } else if (Array.isArray(pickupDays)) {
    return pickupDays.some(day => day.toLowerCase() === today.toLowerCase());
  }
  
  return false;
};

/**
 * Get dismissal time based on current day
 */
export const getDismissalTime = (school) => {
  const today = getCurrentDay();
  if (today === 'Wednesday') {
    return school.dismissalTime?.wednesday || '2:15 PM';
  }
  return school.dismissalTime?.normal || '3:15 PM';
};

/**
 * Calculate camp end date based on duration
 */
export const calculateCampEndDate = (startDate, duration) => {
  if (!startDate || !duration) return null;
  
  const start = new Date(startDate);
  const daysToAdd = duration === '2-days' ? 1 
                   : duration === '3-days' ? 2
                   : duration === '4-days' ? 3
                   : duration === '1-week' ? 4
                   : duration === '2-weeks' ? 9
                   : 0;
  
  const end = new Date(start);
  end.setDate(start.getDate() + daysToAdd);
  
  return end.toISOString().split('T')[0];
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (minimum 10 digits)
 */
export const isValidPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= VALIDATION.MIN_PHONE_LENGTH;
};

/**
 * Get Stripe payment link
 */
export const getStripePaymentLink = (programId, daysPerWeek, stripeLinks) => {
  if (programId === 'saturday-classes') {
    return stripeLinks['saturday-classes']['monthly'];
  } else if (programId === 'after-school-pickup') {
    return stripeLinks['after-school-pickup'][daysPerWeek];
  } else if (programId === 'after-school-dropoff') {
    return stripeLinks['after-school-dropoff'][daysPerWeek];
  } else if (programId === 'full-day-camp') {
    return stripeLinks['full-day-camp'][daysPerWeek];
  }
  return null;
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

/**
 * Open Google Maps navigation
 */
export const openGoogleMaps = (address) => {
  window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
};

/**
 * Generate backup filename
 */
export const getBackupFilename = () => {
  return `stemtree-backup-${new Date().toISOString().split('T')[0]}.json`;
};

/**
 * Export data to JSON file
 */
export const exportToJSON = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export students to CSV
 */
export const exportToCSV = (students, filename) => {
  const exportData = students.map(student => ({
    'Student Name': `${student.firstName} ${student.lastName}`,
    'Grade': student.grade || '-',
    'Program': student.programSelection || '-',
    'Medical Conditions': student.medicalConditions || 'None',
    'Parent Name': student.parentName || '-',
    'Parent Phone': student.parentPhone || '-',
    'Parent Email': student.parentEmail || '-',
    'School': student.schoolName || '-',
    'Pickup Days': student.pickupDays || '-',
    'Form Status': isFormValid(student.submissionDate) ? 'Valid' : 'Expired',
    'Authorized Pickup': student.authorizedPickup || '-'
  }));

  const headers = Object.keys(exportData[0]);
  const csvContent = [
    headers.join(','),
    ...exportData.map(row => headers.map(header => `"${row[header]}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Group students by school
 */
export const groupStudentsBySchool = (students) => {
  return students.reduce((acc, student) => {
    const school = student.schoolName || 'Unknown School';
    if (!acc[school]) {
      acc[school] = [];
    }
    acc[school].push(student);
    return acc;
  }, {});
};

/**
 * Count students by status
 */
export const countStudentsByStatus = (students) => {
  return students.reduce((acc, student) => {
    const status = student.status || STUDENT_STATUS.PENDING;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
};

/**
 * Filter students by search term
 */
export const filterStudentsBySearch = (students, searchTerm) => {
  if (!searchTerm) return students;
  
  const search = searchTerm.toLowerCase();
  return students.filter(student => 
    student.firstName?.toLowerCase().includes(search) ||
    student.lastName?.toLowerCase().includes(search) ||
    student.parentName?.toLowerCase().includes(search) ||
    student.schoolName?.toLowerCase().includes(search)
  );
};

/**
 * Sort students by name
 */
export const sortStudentsByName = (students, ascending = true) => {
  return [...students].sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
    return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });
};

/**
 * Generate notification message
 */
export const generateNotificationMessage = (type, studentName, details = '') => {
  const messages = {
    'picked-up': `${studentName} picked up from school ${details}`,
    'delivered': `${studentName} delivered to Stemtree ${details}`,
    'registration': `New student ${studentName} registered`,
    'alert': `Driver alert: ${details}`
  };
  return messages[type] || `Update for ${studentName}`;
};

/**
 * Debounce function for search inputs
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Format timestamp
 */
export const formatTimestamp = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get time only from timestamp
 */
export const getTimeOnly = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// ==========================================
// GEOLOCATION HELPERS
// ==========================================

/**
 * Calculate distance between two coordinates (in feet)
 */
export const calculateDistance = (coords1, coords2) => {
  const R = 20902231; // Earth's radius in feet
  const dLat = toRadians(coords2.lat - coords1.lat);
  const dLng = toRadians(coords2.lng - coords1.lng);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(coords1.lat)) * Math.cos(toRadians(coords2.lat)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
};

const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Get current GPS position with retry for better accuracy
 * Attempts multiple times to get the best possible accuracy
 */
export const getCurrentPosition = (maxAttempts = 3) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }
    
    let attempts = 0;
    let bestPosition = null;
    
    const tryGetPosition = () => {
      attempts++;
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          
          console.log(`GPS attempt ${attempts}: accuracy ${Math.round(coords.accuracy)}m`);
          
          // Accept if accuracy is good (< 50m) or we've tried enough times
          if (coords.accuracy < 50 || attempts >= maxAttempts) {
            const finalPosition = bestPosition && bestPosition.accuracy < coords.accuracy ? bestPosition : coords;
            console.log(`✓ Using position with ${Math.round(finalPosition.accuracy)}m accuracy`);
            resolve(finalPosition);
          } else {
            // Save best position so far
            if (!bestPosition || coords.accuracy < bestPosition.accuracy) {
              bestPosition = coords;
            }
            
            // Try again after a short delay
            setTimeout(tryGetPosition, 1000);
          }
        },
        (error) => {
          let message = 'Unable to get your location. ';
          
          switch(error.code) {
            case 1: // PERMISSION_DENIED
              message = 'Location access denied. On iPhone: Settings → Safari → Location → Allow. On Android: Allow location access when prompted.';
              break;
            case 2: // POSITION_UNAVAILABLE
              message = 'Location unavailable. Please check that Location Services is enabled in your device settings and try again.';
              break;
            case 3: // TIMEOUT
              message = 'Location request timed out. Please ensure you have a clear view of the sky (step outside if needed) and try again.';
              break;
            default:
              message = 'Location error. Please check Location Services in your device settings and try again.';
          }
          
          console.error('Geolocation error:', error.code, error.message);
          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // 15 seconds - longer for iPhone
          maximumAge: 0   // Don't use cached position
        }
      );
    };
    
    tryGetPosition();
  });
};

/**
 * Verify staff is within allowed location
 * Returns detailed verification info including accuracy warnings
 */
export const verifyLocation = (staffCoords, allowedLocation) => {
  const distance = calculateDistance(staffCoords, allowedLocation.coords);
  const withinRange = distance <= allowedLocation.radius;
  
  // Warn if GPS accuracy is poor
  const accuracyWarning = staffCoords.accuracy > 100 
    ? `⚠️ Low GPS accuracy (${Math.round(staffCoords.accuracy)}m). Location may be inaccurate.`
    : null;
  
  return {
    withinRange,
    distance: Math.round(distance),
    locationName: allowedLocation.name,
    accuracy: Math.round(staffCoords.accuracy),
    accuracyWarning
  };
};
/**
 * Format time duration (minutes to hours/minutes)
 */
export const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}m`;
  }
};

/**
 * Calculate if break is required
 */
export const checkBreakRequirement = (hoursWorked, breaksTaken) => {
  const restBreakRequired = hoursWorked >= 4;
  const restBreakTaken = breaksTaken.some(b => b.type === 'paid-rest');
  
  return {
    restBreakRequired,
    restBreakTaken,
    needsRestBreak: restBreakRequired && !restBreakTaken,
    hoursUntilBreak: restBreakRequired ? 0 : (4 - hoursWorked).toFixed(1)
  };
};